import { useQuery } from "@tanstack/react-query";
import type {
    Recipient,
    Attachment,
    DeliveredMessages,
    DeliveredMessagesAttachments,
    EffectivePlatformLimits,
} from "../API";
import type { DeliveredMessageListItem, LegacyMessageListItem } from "../types/message";
import { queryKeys } from "./queryKeys";
import {
    fetchLegacyMessagesByUserId, fetchDeliveredMessagesByRecipientEmail, fetchDeliveredMessagesBySender, fetchEffectivePlatformLimitsByUserId, fetchUserPlanByUserId,
    fetchDeliveredMessageAttachmentsByDeliveredMessageId,
    fetchUserSettingsByUserId,

} from "./fetchers";
import type { UserSettings } from "./fetchers";
import { fetchRecipientsByUserId, fetchAttachmentsByUserId } from "./fetchers";
import {BillingPlan} from "../utilities/billing";

// All recipients (even unlinked)
export function useRecipients(userId: string, enabled: boolean) {
    const uid = (userId ?? "").trim();

    return useQuery<Recipient[], Error>({
        queryKey: queryKeys.recipients(uid),
        enabled: enabled && !!uid,
        queryFn: () => fetchRecipientsByUserId(uid),
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

// All attachments/files (even unlinked)
export function useAttachments(userId: string, enabled: boolean) {
    const uid = (userId ?? "").trim();

    return useQuery<Attachment[], Error>({
        queryKey: queryKeys.attachments(uid),
        enabled: enabled && !!uid,
        queryFn: () => fetchAttachmentsByUserId(uid),
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

/**
 * Canonical legacy messages query:
 * Messages + Recipients + Attachments metadata
 */
export function useLegacyMessages(userId: string, enabled: boolean) {
    const uid = (userId ?? "").trim();

    return useQuery<LegacyMessageListItem[], Error>({
        queryKey: queryKeys.legacyMessages(uid),
        enabled: enabled && !!uid,
        queryFn: () => fetchLegacyMessagesByUserId(uid),
        staleTime: 5 * 60 * 1000,  // tune later
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

/**
 * Canonical delivered/inherited messages query (inbox).
 */
export function useDeliveredMessages(recipientEmail: string, enabled: boolean) {
    const email = (recipientEmail ?? "").trim().toLowerCase();

    return useQuery<DeliveredMessageListItem[], Error>({
        queryKey: queryKeys.deliveredMessages(email),
        enabled: enabled && !!email,
        queryFn: () => fetchDeliveredMessagesByRecipientEmail(email),
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export function useDeliveredMessagesBySender(userId: string, enabled: boolean) {
    const uid = (userId ?? "").trim();

    return useQuery<DeliveredMessages[], Error>({
        queryKey: queryKeys.deliveredMessagesBySender(uid),
        enabled: enabled && !!uid,
        queryFn: () => fetchDeliveredMessagesBySender(uid),
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export function useDeliveredMessageAttachments(deliveredMessageId: string, enabled: boolean) {
    const messageId = (deliveredMessageId ?? "").trim();

    return useQuery<DeliveredMessagesAttachments[], Error>({
        queryKey: queryKeys.deliveredMessageAttachments(messageId),
        enabled: enabled && !!messageId,
        queryFn: () => fetchDeliveredMessageAttachmentsByDeliveredMessageId(messageId),
        staleTime: Infinity,// 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

/**
 * User's Plan Details
 */
export function useUserPlan(userId: string, enabled: boolean) {
    const uid = (userId ?? "").trim();

    return useQuery<BillingPlan | null, Error>({
        queryKey: queryKeys.plan(uid),
        enabled: enabled && !!uid,
        queryFn: () => fetchUserPlanByUserId(uid),
        staleTime: Infinity, //60 * 60 * 1000,
        gcTime: 90 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export function useEffectivePlatformLimits(userId: string, enabled: boolean) {
    const uid = (userId ?? "").trim();

    return useQuery<EffectivePlatformLimits | null, Error>({
        queryKey: queryKeys.effectivePlatformLimits(uid),
        enabled: enabled && !!uid,
        queryFn: () => fetchEffectivePlatformLimitsByUserId(uid),
        staleTime: Infinity,
        gcTime: 90 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export function useUserSettings(userId: string, enabled: boolean) {
    const uid = (userId ?? "").trim();

    return useQuery<UserSettings, Error>({
        queryKey: queryKeys.userSettings(uid),
        enabled: enabled && !!uid,
        queryFn: () => fetchUserSettingsByUserId(uid),
        staleTime: Infinity,
        gcTime: 90 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

